import { useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { Medal } from 'lucide-react';
import { db } from '../../../firebase/config';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { cn } from '../../../lib/utils';

const medalClass = {
  1: 'text-gold',
  2: 'text-silver',
  3: 'text-bronze',
};

const Placement = ({ placement }) => {
  if (!placement || placement === '-') return <span className="text-muted-foreground/40">—</span>;
  const top = placement <= 3;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 font-mono tabular-nums',
        top ? `${medalClass[placement]} font-semibold` : 'text-foreground/80'
      )}
    >
      {top && <Medal className="h-3.5 w-3.5" />}
      {placement}
    </span>
  );
};

const StageOne = ({ tournamentId, mode }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const snap = await getDocs(
          query(
            collection(db, 'tournaments', tournamentId, 'groups'),
            where('mode', '==', mode),
            where('stage', '==', 1)
          )
        );
        if (!cancelled) {
          setGroups(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        }
      } catch {
        // ignore — anonymous reads may be restricted
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [tournamentId, mode]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        {Array.from({ length: 2 }).map((_, i) => (
          <div
            key={i}
            className="h-48 animate-pulse rounded-md border border-border/60 bg-muted/10"
          />
        ))}
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className="border border-dashed border-border/60 px-6 py-12 text-center text-sm text-muted-foreground">
        No groups published for this mode yet.
      </div>
    );
  }

  const sortedGroups = [...groups].sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  return (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
      {sortedGroups.map((group) => {
        const maps = group.maps || [];
        const rankedPlayers = (group.players || [])
          .map((p) => {
            const totalPoints = maps.reduce((total, map) => {
              const r = map.rankings?.find((rk) => rk.playerId === p.id);
              return total + (r?.placement || 0);
            }, 0);
            return { ...p, totalPoints };
          })
          .sort((a, b) => a.totalPoints - b.totalPoints);

        return (
          <Card key={group.id} className="overflow-hidden border-border/60 bg-background/40">
            <CardHeader className="border-b border-border/60 pb-3">
              <CardTitle className="font-display text-lg">{group.name}</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border/60">
                      <th className="w-14 px-3 py-2.5 text-left font-mono text-[10px] font-medium uppercase tracking-[0.25em] text-muted-foreground">
                        Rank
                      </th>
                      <th className="px-3 py-2.5 text-left font-mono text-[10px] font-medium uppercase tracking-[0.25em] text-muted-foreground">
                        Player
                      </th>
                      {maps.map((map) => (
                        <th
                          key={map.id}
                          className="min-w-[70px] px-3 py-2.5 text-center font-mono text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground"
                        >
                          {map.name}
                        </th>
                      ))}
                      <th className="w-20 px-3 py-2.5 text-center font-mono text-[10px] font-medium uppercase tracking-[0.25em] text-muted-foreground">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rankedPlayers.map((player, index) => (
                      <tr key={player.id} className="border-b border-border/40 last:border-0">
                        <td className="px-3 py-2.5 font-mono text-xs tabular-nums text-muted-foreground">
                          {String(index + 1).padStart(2, '0')}
                        </td>
                        <td className="px-3 py-2.5 font-display text-sm font-medium">
                          {player.name}
                        </td>
                        {maps.map((map) => {
                          const placement = map.rankings?.find(
                            (r) => r.playerId === player.id
                          )?.placement;
                          return (
                            <td key={map.id} className="px-3 py-2.5 text-center">
                              <Placement placement={placement} />
                            </td>
                          );
                        })}
                        <td className="px-3 py-2.5 text-center font-mono text-sm font-semibold tabular-nums">
                          {player.totalPoints}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default StageOne;
